import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useFollowUser, useUnfollowUser, useFollowStatus } from '../lib/api';
import { useTranslation } from '../hooks/useTranslation';
import ErrorToast from './ErrorToast';
import SuccessToast from './SuccessToast';

interface FollowButtonProps {
  userId: string;
  username?: string;
  /** When provided (e.g. from list API), skip per-user follow-status request to avoid N+1 */
  isFollowingFromList?: boolean;
  /** When true, render nothing if user is already following (e.g. Reels: show button only when not followed) */
  hideWhenFollowing?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const FollowButton: React.FC<FollowButtonProps> = ({ 
  userId, 
  username, 
  isFollowingFromList,
  hideWhenFollowing = false,
  className = '',
  size = 'md'
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  if (currentUser && userId === currentUser.id?.toString()) {
    return null;
  }

  // When list API already returned is_following, skip per-user follow-status request (avoids N+1)
  const { data: followStatus, isError: statusError } = useFollowStatus(userId, {
    enabled: isAuthenticated && !!userId && isFollowingFromList === undefined
  });

  const followMutation = useFollowUser({
    onSuccess: () => {
      setSuccessMessage(t('follow.successFollow', { name: username || t('follow.user') }));
      setShowSuccess(true);
    },
    onError: (error: any) => {
      setErrorMessage(error.message || t('follow.errorFollow'));
      setShowError(true);
    }
  });

  const unfollowMutation = useUnfollowUser({
    onSuccess: () => {
      setSuccessMessage(t('follow.successUnfollow', { name: username || t('follow.user') }));
      setShowSuccess(true);
    },
    onError: (error: any) => {
      setErrorMessage(error.message || t('follow.errorUnfollow'));
      setShowError(true);
    }
  });

  const isFollowing = isAuthenticated && (
    isFollowingFromList === true ||
    (isFollowingFromList === undefined && !statusError && followStatus?.following === true)
  );
  const isLoading = followMutation.isPending || unfollowMutation.isPending;

  if (hideWhenFollowing && isFollowing) {
    return null;
  }

  const handleFollowClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isFollowing) {
      unfollowMutation.mutate(userId);
    } else {
      followMutation.mutate(userId);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-base';
      default:
        return 'px-4 py-2 text-sm';
    }
  };

  const baseClasses = `
    ${getSizeClasses()}
    font-semibold rounded-lg transition-all duration-300 ease-in-out
    disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900
    shadow-sm hover:shadow-md transform-gpu
    flex items-center justify-center gap-2 min-w-[100px]
  `;

  const followingClasses = `
    ${baseClasses}
    bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600
    text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600
    hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/30 dark:hover:to-red-800/30
    hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-600
    focus:ring-gray-400 dark:focus:ring-gray-500
  `;

  const notFollowingClasses = `
    ${baseClasses}
    bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
    text-white border border-transparent
    focus:ring-blue-400 dark:focus:ring-blue-500
    hover:scale-105
  `;

  return (
    <>
      <ErrorToast
        message={errorMessage}
        isVisible={showError}
        onClose={() => setShowError(false)}
      />
      <SuccessToast
        message={successMessage}
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
      />

      <motion.button
        whileHover={{ scale: isLoading ? 1 : 1.02 }}
        whileTap={{ scale: isLoading ? 1 : 0.95 }}
        onClick={handleFollowClick}
        disabled={isLoading}
        className={`group ${isFollowing ? followingClasses : notFollowingClasses} ${className}`}
      >
        {isLoading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </>
        ) : (
          <div className="flex items-center gap-2">
            {isFollowing ? (
              <>
                <div className="group-hover:hidden flex items-center gap-2">
                  <span>{t('follow.following')}</span>
                </div>
                <div className="hidden group-hover:flex items-center gap-2">
                  <span>{t('follow.unfollow')}</span>
                </div>
              </>
            ) : (
              <>
                <span>{t('follow.follow')}</span>
              </>
            )}
          </div>
        )}
      </motion.button>
    </>
  );
};

export default FollowButton;